import React, { useState, useEffect, useMemo, useRef } from 'react';
import CustomDatePicker from '../shared/CustomDatePicker';
import {
    Calendar, User, Users, Truck, Hash, Plus, Trash2, Save, Printer,
    ChevronLeft, Edit2, Check, X, ArrowLeft, MoreHorizontal,
    ShoppingBag, CreditCard, DollarSign, Wallet, MapPin,
    Clock, MessageSquare, Phone, UserPlus, Info, CheckCircle2,
    Eye,
    Search, AlertCircle, FileText, LayoutList, ClipboardList,
    Download, Loader2
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { useCustomer } from '../../../../hooks/useCustomer';
import {
    getDailySalesReport,
    getDailySalesReports,
    createDailySalesReport,
    deleteDailySalesReport,
    downloadConsolidatedPdf,
    handleApiError
} from '../../../../services/api/dailySalesReportApi';
import { getSalesPersons } from '../../../../services/api/lookupApi';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { format } from 'date-fns';

const COMMON_EXPENSES = [
    'Fuel',
    'Salik / Toll',
    'Parking',
    'Food',
    'Vehicle Maintenance',
    'Local Purchase',
    'Miscellaneous'
];

const VISIT_TYPES = [
    'In Person Visit',
    'Over Phone',
    'Email/WhatsApp'
];

const VISIT_OUTCOMES = [
    'Order Placed',
    'Visit Again',
    'Interested',
    'Pending Review',
    'Rejected'
];

const StaffReportDetailCard = ({ report, detailedData, setDetailedData, handleEditReport, handleViewReport }) => {
    const [details, setDetails] = useState(detailedData[report.publicId] || null);
    const [loading, setLoading] = useState(!details);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (detailedData[report.publicId]) {
            setDetails(detailedData[report.publicId]);
            setLoading(false);
            return;
        }

        let isMounted = true;
        const fetchDetails = async () => {
            try {
                setLoading(true);
                const data = await getDailySalesReport(report.publicId);
                if (isMounted) {
                    setDetails(data);
                    setDetailedData(prev => ({ ...prev, [report.publicId]: data }));
                }
            } catch (err) {
                console.error("Error loading report details:", err);
                if (isMounted) {
                    setError("Failed to load details");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        fetchDetails();
        return () => { isMounted = false; };
    }, [report.publicId, detailedData[report.publicId], setDetailedData]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 gap-3 text-gray-400 font-bold uppercase text-[10px] tracking-widest bg-gray-50/50 rounded-3xl border border-gray-100">
                <LoadingSpinner size="sm" />
                Loading detailed records...
            </div>
        );
    }

    if (error || !details) {
        return (
            <div className="p-6 bg-red-50 text-red-600 rounded-3xl border border-red-100 text-xs font-bold text-center">
                {error || "Could not load details."}
            </div>
        );
    }

    const sales = details.salesRecords || [];
    const expenses = details.expenseRecords || [];
    const visits = details.clientVisits || [];

    // Calculate Sales Totals safely supporting both b2bAmount and b2bCash/Bank
    const totalCash = sales.reduce((sum, s) => sum + (Number(s.cashAmount) || 0), 0);
    const totalCredit = sales.reduce((sum, s) => sum + (Number(s.creditAmount) || 0), 0);
    const totalB2bCash = sales.reduce((sum, s) => sum + (Number(s.b2bCashAmount) || Number(s.b2bAmount) || 0), 0);
    const totalB2bBank = sales.reduce((sum, s) => sum + (Number(s.b2bBankAmount) || 0), 0);
    const totalBank = sales.reduce((sum, s) => sum + (Number(s.bankAmount) || 0), 0);
    const grossSales = sales.reduce((sum, s) => {
        return sum + (Number(s.cashAmount) || 0) + (Number(s.creditAmount) || 0) + (Number(s.bankAmount) || 0);
    }, 0);

    const totalExp = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

    const formatVisitDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return format(new Date(dateStr), 'dd/MM/yyyy');
        } catch (e) {
            return dateStr;
        }
    };

    return (
        <div className="space-y-8 mt-4 pt-6 border-t border-gray-100 animate-in slide-in-from-top-4 duration-300">
            {/* Quick Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50/30 p-5 rounded-2xl border border-blue-100/50">
                    <p className="text-[9px] font-black text-blue-500 uppercase mb-3 tracking-[0.15em]">Sale Summary</p>
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">Opening Cash:</span>
                            <span className="text-gray-900">AED {Number(details.openingCash || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">Cash Collected:</span>
                            <span className="text-gray-900">AED {Number(details.totalCashAvailable || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">Credit Sales:</span>
                            <span className="text-gray-900">AED {Number(details.totalCredit || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">B2B Cash:</span>
                            <span className="text-blue-600">AED {Number(details.totalB2bCash || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">B2B Bank:</span>
                            <span className="text-purple-600">AED {Number(details.totalB2bBank || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">Bank Transfer:</span>
                            <span className="text-blue-600">AED {Number(details.totalBank || 0).toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-red-50/30 p-5 rounded-2xl border border-red-100/50">
                    <p className="text-[9px] font-black text-red-500 uppercase mb-3 tracking-[0.15em]">Logistics</p>
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">Helper Name:</span>
                            <span className="text-gray-900">{details.helperName || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">Total Expenses:</span>
                            <span className="text-red-600">AED {Number(details.totalExpenses || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">Odometer Start:</span>
                            <span className="text-gray-700">{details.odometerStart || 0} KM</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-gray-500">Odometer End:</span>
                            <span className="text-gray-700">{details.odometerEnd || 0} KM</span>
                        </div>
                        <div className="flex justify-between text-xs font-black border-t border-gray-100 pt-2 text-gray-900">
                            <span className="flex items-center gap-1.5"><MapPin size={12} className="text-gray-400" /> Distance Traveled:</span>
                            <span>
                                {details.odometerStart !== undefined && details.odometerEnd !== undefined
                                    ? `${Number(details.odometerEnd) - Number(details.odometerStart)} KM`
                                    : 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-900 p-5 rounded-2xl flex flex-col justify-center items-center text-white shadow-xl shadow-gray-200">
                    <span className="text-[8px] font-black uppercase text-gray-400 mb-2 tracking-[0.2em]">EOD Liquid Net Position</span>
                    <span className="text-3xl font-black text-blue-400">AED {Number(details.finalNetPosition || 0).toFixed(2)}</span>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-[9px] font-black uppercase tracking-wider">
                            {sales.filter(s => 
                                ((Number(s.cashAmount) || 0) > 0 || (Number(s.bankAmount) || 0) > 0 || (Number(s.creditAmount) || 0) > 0) &&
                                !((Number(s.b2bCashAmount) || 0) > 0 || (Number(s.b2bBankAmount) || 0) > 0)
                            ).length} Sales
                        </span>
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-[9px] font-black uppercase tracking-wider">
                            {visits.length} Visits
                        </span>
                    </div>
                </div>
            </div>

            {/* Sales Records Section */}
            {sales.length > 0 && (
                <div className="space-y-3">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] flex items-center gap-2">
                        <ShoppingBag size={14} className="text-blue-500" /> Sales Transactions
                    </h4>
                    <div className="border border-gray-100 rounded-2xl overflow-x-auto shadow-sm bg-white hidden md:block">
                        <table className="w-full text-xs text-left min-w-[900px]">
                            <thead className="bg-gray-50 text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                <tr>
                                    <th className="px-4 py-3 min-w-[200px]">Client</th>
                                    <th className="px-3 py-3 text-center w-40">Order / Invoice #</th>
                                    <th className="px-3 py-3 text-right">Cash</th>
                                    <th className="px-3 py-3 text-right">Credit</th>
                                    <th className="px-3 py-3 text-right">B2B Cash</th>
                                    <th className="px-3 py-3 text-right">B2B Bank</th>
                                    <th className="px-3 py-3 text-right">Bank</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sales.map((s, idx) => {
                                    return (
                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-4 py-3 font-bold text-gray-900">{s.clientName}</td>
                                            <td className="px-3 py-3 text-center font-medium text-gray-500 whitespace-nowrap">
                                                {(() => {
                                                     const orderRef = s.orderNo ? s.orderNo : null;
                                                     const invRef = s.invoiceNo ? s.invoiceNo : null;
                                                     if (!orderRef && !invRef) return <span className="text-gray-400">-</span>;
                                                     return (
                                                         <div>
                                                             {orderRef && (
                                                                 <span className="font-bold text-gray-900 block text-xs">
                                                                     <span className="text-[10px] text-blue-600 font-bold mr-1">Order</span> {orderRef.replace(/^#/, '')}
                                                                 </span>
                                                             )}
                                                             {invRef && (
                                                                 <span className="text-[10px] text-gray-500 block mt-0.5">
                                                                     <span className="text-[9px] text-gray-400 font-normal mr-1">Inv:</span>{invRef}
                                                                 </span>
                                                             )}
                                                         </div>
                                                     );
                                                })()}
                                            </td>
                                            <td className="px-3 py-3 text-right font-semibold text-gray-900">{s.cashAmount ? Number(s.cashAmount).toFixed(2) : '-'}</td>
                                            <td className="px-3 py-3 text-right font-semibold text-gray-500">{s.creditAmount ? Number(s.creditAmount).toFixed(2) : '-'}</td>
                                            <td className="px-3 py-3 text-right font-semibold text-blue-600">{s.b2bCashAmount || s.b2bAmount ? Number(s.b2bCashAmount || s.b2bAmount).toFixed(2) : '-'}</td>
                                            <td className="px-3 py-3 text-right font-semibold text-purple-600">{s.b2bBankAmount ? Number(s.b2bBankAmount).toFixed(2) : '-'}</td>
                                            <td className="px-3 py-3 text-right font-semibold text-blue-600">{s.bankAmount ? Number(s.bankAmount).toFixed(2) : '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-gray-50 font-black text-gray-900 text-[10px] border-t-2 border-gray-100">
                                <tr>
                                    <td colSpan={2} className="px-4 py-3 text-right uppercase tracking-wider">Totals:</td>
                                    <td className="px-3 py-3 text-right">{totalCash > 0 ? `AED ${totalCash.toFixed(2)}` : '-'}</td>
                                    <td className="px-3 py-3 text-right text-gray-500">{totalCredit > 0 ? `AED ${totalCredit.toFixed(2)}` : '-'}</td>
                                    <td className="px-3 py-3 text-right text-blue-600">{totalB2bCash > 0 ? `AED ${totalB2bCash.toFixed(2)}` : '-'}</td>
                                    <td className="px-3 py-3 text-right text-purple-600">{totalB2bBank > 0 ? `AED ${totalB2bBank.toFixed(2)}` : '-'}</td>
                                    <td className="px-3 py-3 text-right text-blue-600">{totalBank > 0 ? `AED ${totalBank.toFixed(2)}` : '-'}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Mobile View Card List */}
                    <div className="space-y-3 md:hidden">
                        {sales.map((s, idx) => {
                            const srTotal = (Number(s.cashAmount) || 0) + (Number(s.creditAmount) || 0) + (Number(s.b2bCashAmount) || Number(s.b2bAmount) || 0) + (Number(s.b2bBankAmount) || 0) + (Number(s.bankAmount) || 0);
                            return (
                                <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm space-y-3">
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0">
                                            <p className="font-bold text-gray-900 text-sm truncate">{s.clientName}</p>
                                            <div className="flex items-center gap-2 text-[10px] font-medium text-gray-600 mt-0.5">
                                                {(() => {
                                                    const orderRef = s.orderNo ? s.orderNo : null;
                                                    const invRef = s.invoiceNo ? s.invoiceNo : null;
                                                    return (
                                                        <>
                                                            {orderRef && <span>Order: <strong className="text-blue-600 font-bold">{orderRef.replace(/^#/, '')}</strong></span>}
                                                            {orderRef && invRef && <span>•</span>}
                                                            {invRef && <span>Invoice: <strong className="text-gray-700">{invRef}</strong></span>}
                                                            {!orderRef && !invRef && <span className="text-gray-400">Order/Invoice: N/A</span>}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block">Total</span>
                                            <span className="text-sm font-black text-blue-600">AED {srTotal.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-50 text-xs">
                                        {Number(s.cashAmount) > 0 && (
                                            <div className="bg-gray-50 p-2 rounded-xl">
                                                <span className="text-[9px] font-black text-gray-400 uppercase block">Cash</span>
                                                <span className="font-bold text-gray-900">AED {Number(s.cashAmount).toFixed(2)}</span>
                                            </div>
                                        )}
                                        {Number(s.creditAmount) > 0 && (
                                            <div className="bg-amber-50/50 p-2 rounded-xl border border-amber-100/50">
                                                <span className="text-[9px] font-black text-amber-500 uppercase block">Credit</span>
                                                <span className="font-bold text-amber-700">AED {Number(s.creditAmount).toFixed(2)}</span>
                                            </div>
                                        )}
                                        {(Number(s.b2bCashAmount) > 0 || Number(s.b2bAmount) > 0) && (
                                            <div className="bg-blue-50/50 p-2 rounded-xl border border-blue-100/50">
                                                <span className="text-[9px] font-black text-blue-500 uppercase block">B2B Cash</span>
                                                <span className="font-bold text-blue-700">AED {Number(s.b2bCashAmount || s.b2bAmount).toFixed(2)}</span>
                                            </div>
                                        )}
                                        {Number(s.b2bBankAmount) > 0 && (
                                            <div className="bg-purple-50/50 p-2 rounded-xl border border-purple-100/50">
                                                <span className="text-[9px] font-black text-purple-500 uppercase block">B2B Bank</span>
                                                <span className="font-bold text-purple-700">AED {Number(s.b2bBankAmount).toFixed(2)}</span>
                                            </div>
                                        )}
                                        {Number(s.bankAmount) > 0 && (
                                            <div className="bg-blue-50/50 p-2 rounded-xl border border-blue-100/50">
                                                <span className="text-[9px] font-black text-blue-500 uppercase block">Bank</span>
                                                <span className="font-bold text-blue-700">AED {Number(s.bankAmount).toFixed(2)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {/* Mobile Summary Card */}
                        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 space-y-2 text-xs">
                            <p className="font-black text-gray-500 uppercase tracking-widest text-[9px]">Sales Totals</p>
                            <div className="space-y-1">
                                {totalCash > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 font-semibold">Total Cash:</span>
                                        <span className="font-bold text-gray-900">AED {totalCash.toFixed(2)}</span>
                                    </div>
                                )}
                                {totalCredit > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 font-semibold">Total Credit:</span>
                                        <span className="font-bold text-gray-500">AED {totalCredit.toFixed(2)}</span>
                                    </div>
                                )}
                                {totalB2bCash > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 font-semibold">Total B2B Cash:</span>
                                        <span className="font-bold text-blue-600">AED {totalB2bCash.toFixed(2)}</span>
                                    </div>
                                )}
                                {totalB2bBank > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 font-semibold">Total B2B Bank:</span>
                                        <span className="font-bold text-purple-600">AED {totalB2bBank.toFixed(2)}</span>
                                    </div>
                                )}
                                {totalBank > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 font-semibold">Total Bank:</span>
                                        <span className="font-bold text-blue-600">AED {totalBank.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between border-t border-gray-200 pt-2 font-black text-sm">
                                    <span className="text-gray-900">Gross Sales:</span>
                                    <span className="text-blue-600">AED {grossSales.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Expenses Section */}
            {expenses.length > 0 && (
                <div className="space-y-3">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] flex items-center gap-2">
                        <CreditCard size={14} className="text-red-500" /> Daily Expenses
                    </h4>
                    <div className="border border-gray-100 rounded-2xl overflow-x-auto shadow-sm bg-white hidden md:block">
                        <table className="w-full text-xs text-left min-w-[600px]">
                            <thead className="bg-gray-50 text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                <tr>
                                    <th className="px-4 py-3">Description</th>
                                    <th className="px-4 py-3 text-right w-44">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {expenses.map((e, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3 font-bold text-gray-900">{e.description}</td>
                                        <td className="px-4 py-3 text-right font-black text-red-600">AED {Number(e.amount).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 font-black text-gray-900 text-[10px] border-t border-gray-100">
                                <tr>
                                    <td className="px-4 py-3 uppercase tracking-wider">Total Expenses:</td>
                                    <td className="px-4 py-3 text-right text-red-600 font-black">AED {totalExp.toFixed(2)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Mobile View Card List */}
                    <div className="space-y-2 md:hidden">
                        {expenses.map((e, idx) => (
                            <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-3.5 shadow-sm flex justify-between items-center text-xs">
                                <span className="font-bold text-gray-900">{e.description}</span>
                                <span className="font-black text-red-600">AED {Number(e.amount).toFixed(2)}</span>
                            </div>
                        ))}
                        <div className="bg-red-50/30 border border-red-100/50 rounded-2xl p-3.5 flex justify-between items-center text-xs font-black">
                            <span className="text-gray-700 uppercase tracking-widest text-[9px]">Total Expenses:</span>
                            <span className="text-red-600">AED {totalExp.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Client Visits Section */}
            {visits.length > 0 && (
                <div className="space-y-3">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] flex items-center gap-2">
                        <MapPin size={14} className="text-green-500" /> Client Visit Log
                    </h4>
                    <div className="border border-gray-100 rounded-2xl overflow-x-auto shadow-sm bg-white hidden md:block">
                        <table className="w-full text-xs text-left min-w-[800px]">
                            <thead className="bg-gray-50 text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                <tr>
                                    <th className="px-4 py-3 min-w-[150px]">Client</th>
                                    <th className="px-3 py-3 text-center w-36">Visit Type</th>
                                    <th className="px-3 py-3 text-center w-32">Outcome</th>
                                    <th className="px-4 py-3">Discussion Notes & Follow-ups</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {visits.map((v, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3.5">
                                            <p className="font-bold text-gray-900">{v.clientName}</p>
                                            {v.customerMobileNumber && <p className="text-[10px] text-gray-400 font-semibold mt-0.5">{v.customerMobileNumber}</p>}
                                        </td>
                                        <td className="px-3 py-3.5 text-center">
                                            <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-lg text-[9px] font-black uppercase tracking-wider">
                                                {v.visitType}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3.5 text-center">
                                            <span className={`px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                                                v.outcomeNotes === 'Order Placed' ? 'bg-green-100 text-green-700' :
                                                v.outcomeNotes === 'Rejected' ? 'bg-red-100 text-red-700' :
                                                v.outcomeNotes ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                                {v.outcomeNotes || 'No Outcome'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <p className="font-medium text-gray-700">{v.notes || '-'}</p>
                                            {v.followUpDetails && (
                                                <p className="text-[9px] text-blue-600 font-black uppercase mt-1 flex items-center gap-1">
                                                    <Clock size={10} /> Follow-up Scheduled: {formatVisitDate(v.followUpDetails)}
                                                </p>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View Card List */}
                    <div className="space-y-3 md:hidden">
                        {visits.map((v, idx) => (
                            <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm space-y-3">
                                <div className="flex justify-between items-start gap-2">
                                    <div className="min-w-0">
                                        <p className="font-bold text-gray-900 text-sm truncate">{v.clientName}</p>
                                        {v.customerMobileNumber && <p className="text-[10px] text-gray-400 font-semibold mt-0.5">{v.customerMobileNumber}</p>}
                                    </div>
                                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                        <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-lg text-[9px] font-black uppercase tracking-wider">
                                            {v.visitType}
                                        </span>
                                        <span className={`px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                                            v.outcomeNotes === 'Order Placed' ? 'bg-green-100 text-green-700' :
                                            v.outcomeNotes === 'Rejected' ? 'bg-red-100 text-red-700' :
                                            v.outcomeNotes ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {v.outcomeNotes || 'No Outcome'}
                                        </span>
                                    </div>
                                </div>
                                {v.notes && (
                                    <div className="bg-gray-50 p-3 rounded-xl text-xs">
                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-wider block mb-1">Discussion Notes</span>
                                        <p className="font-medium text-gray-700 leading-snug break-words">{v.notes}</p>
                                    </div>
                                )}
                                {v.followUpDetails && (
                                    <div className="flex items-center gap-1.5 text-[9px] font-black text-blue-600 uppercase bg-blue-50/50 p-2 rounded-lg border border-blue-100/30">
                                        <Clock size={11} />
                                        <span>Follow-up: {formatVisitDate(v.followUpDetails)}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const DailySalesReportView = () => {
    const { user } = useAuth();
    const isManagement = user?.realmRoles?.some(r => ['ADMIN', 'SUPPLIER_OWNER', 'SALES_MANAGER', 'SYSTEM_ADMIN'].includes(r)) || false;
    const isSalesPerson = user?.primaryRole === 'SALES_PERSON' || user?.realmRoles?.includes('SALES_PERSON');
    const isSalesPersonOnly = isSalesPerson && !isManagement;

    // List View State
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // 'list' or 'form'
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState({
        start: format(new Date(), 'yyyy-MM-dd'),
        end: format(new Date(), 'yyyy-MM-dd')
    });
    const [staffFilter, setStaffFilter] = useState('');
    const [expandedReportId, setExpandedReportId] = useState(null);
    const [detailedData, setDetailedData] = useState({}); // Cache for full report details
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showPdfMenu, setShowPdfMenu] = useState(false);
    const [showPdfMenuList, setShowPdfMenuList] = useState(false);
    const [showConfirmBack, setShowConfirmBack] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [existingReportForDate, setExistingReportForDate] = useState(null);

    const safeFormatDate = (dateStr, formatStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return 'N/A';
            return format(date, formatStr);
        } catch (e) {
            return 'N/A';
        }
    };

    const filteredReports = useMemo(() => {
        return (reports || []).filter(r => {
            const name = (r.salesPersonName || '').toLowerCase();
            const vehicle = (r.vehicleNo || '').toLowerCase();
            const helper = (r.helperName || '').toLowerCase();
            const search = searchQuery.toLowerCase();

            const matchesSearch = name.includes(search) ||
                vehicle.includes(search) ||
                helper.includes(search);

            const matchesStaff = !staffFilter || r.salesPersonPublicId === staffFilter;

            return matchesSearch && matchesStaff;
        });
    }, [reports, searchQuery, staffFilter]);

    const dailySummary = useMemo(() => {
        const summary = {
            totalSales: 0,
            totalCash: 0,
            totalExpenses: 0,
            netPosition: 0,
            totalVisits: 0
        };

        filteredReports.forEach(r => {
            summary.totalSales += Number(r.totalSaleSummary) || 0;
            summary.totalCash += Number(r.totalCashAvailable) || 0;
            summary.totalExpenses += Number(r.totalExpenses) || 0;
            summary.netPosition += Number(r.finalNetPosition) || 0;
            summary.totalVisits += Number(r.totalVisits) || 0;
        });

        return summary;
    }, [filteredReports]);

    // Grouped Collapsible and Actions State
    const [expandedReports, setExpandedReports] = useState({});
    const [expandedStaff, setExpandedStaff] = useState({});

    const toggleReportExpand = (reportId) => {
        setExpandedReports(prev => ({
            ...prev,
            [reportId]: !prev[reportId]
        }));
    };

    const toggleStaffExpand = (staffId) => {
        setExpandedStaff(prev => ({
            ...prev,
            [staffId]: !prev[staffId]
        }));
    };

    const isSingleDay = dateRange.start && dateRange.end && dateRange.start === dateRange.end;

    const groupedReports = useMemo(() => {
        if (isSalesPersonOnly) {
            // Group by date for sales persons
            const groups = {};
            filteredReports.forEach(r => {
                const date = r.reportDate || 'No Date';
                if (!groups[date]) groups[date] = [];
                groups[date].push(r);
            });
            return Object.keys(groups)
                .sort((a, b) => a.localeCompare(b))  // ascending: oldest date first
                .map(date => ({
                    type: 'date',
                    key: date,
                    label: safeFormatDate(date, 'EEEE, MMM dd, yyyy'),
                    // Within date: sort by submission time ascending (oldest entry first, newest last)
                    reports: groups[date].sort((a, b) => {
                        const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                        const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                        return tA - tB;
                    })
                }));
        } else {
            // Group by Sales Person for managers/admins
            const groups = {};
            filteredReports.forEach(r => {
                const personId = r.salesPersonPublicId || 'Unknown';
                const personName = (r.salesPersonName && r.salesPersonName !== 'Unknown Staff') ? r.salesPersonName : (r.salesPersonPublicId ? `Staff (${r.salesPersonPublicId.slice(0, 8)})` : 'Staff Member');
                if (!groups[personId]) {
                    groups[personId] = {
                        publicId: personId,
                        name: personName,
                        reports: []
                    };
                }
                groups[personId].reports.push(r);
            });
            return Object.values(groups)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(group => {
                    // Within person: sort by submission time ascending (oldest entry first, newest last)
                    group.reports.sort((a, b) => {
                        const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                        const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                        return tA - tB;
                    });
                    return {
                        type: 'person',
                        key: group.publicId,
                        name: group.name,
                        reports: group.reports
                    };
                });
        }
    }, [filteredReports, isSalesPersonOnly]);

    const handleEditReport = async (report) => {
        try {
            setLoading(true);
            const hasDetails = detailedData[report.publicId] && Array.isArray(detailedData[report.publicId].salesRecords);
            const fullData = hasDetails ? detailedData[report.publicId] : await getDailySalesReport(report.publicId);
            
            if (!hasDetails) {
                setDetailedData(prev => ({ ...prev, [report.publicId]: fullData }));
            }
            
            setCurrentReport(fullData);
            setFormData({
                ...fullData,
                reportDate: fullData.reportDate || format(new Date(), 'yyyy-MM-dd'),
                salesPersonPublicId: fullData.salesPersonPublicId,
                vehicleNo: fullData.vehicleNo || '',
                salesRecords: (fullData.salesRecords || []).map(s => ({ ...s })),
                expenseRecords: (fullData.expenseRecords || []).map(e => ({ ...e })),
                clientVisits: (fullData.clientVisits || []).map(v => ({
                    ...v,
                    followUpRequired: !!v.followUpDetails
                }))
            });
            setIsDirty(false); // Reset dirty flag
            setExistingReportForDate(null);
            setError(null);
            setView('form');
        } catch (err) {
            console.error("Error loading report for edit:", err);
            alert("Failed to load report data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleViewReport = async (report) => {
        try {
            setLoading(true);
            const hasDetails = detailedData[report.publicId] && Array.isArray(detailedData[report.publicId].salesRecords);
            const fullData = hasDetails ? detailedData[report.publicId] : await getDailySalesReport(report.publicId);
            
            if (!hasDetails) {
                setDetailedData(prev => ({ ...prev, [report.publicId]: fullData }));
            }
            
            setFormData({
                ...fullData,
                reportDate: fullData.reportDate || format(new Date(), 'yyyy-MM-dd'),
                salesPersonPublicId: fullData.salesPersonPublicId,
                vehicleNo: fullData.vehicleNo || '',
                salesRecords: (fullData.salesRecords || []).map(s => ({ ...s })),
                expenseRecords: (fullData.expenseRecords || []).map(e => ({ ...e })),
                clientVisits: (fullData.clientVisits || []).map(v => ({
                    ...v,
                    followUpRequired: !!v.followUpDetails
                }))
            });
            setShowPrintPreview(true);
        } catch (err) {
            console.error("Error loading report for view:", err);
            alert("Failed to load report details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Form View State
    const [currentReport, setCurrentReport] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [showPrintPreview, setShowPrintPreview] = useState(false);

    // Form Data
    const [formData, setFormData] = useState({
        reportDate: format(new Date(), 'yyyy-MM-dd'),
        salesPersonPublicId: user?.publicId || '',
        helperName: '',
        vehicleNo: '',
        odometerStart: '',
        odometerEnd: '',
        openingCash: '',
        salesRecords: [],
        expenseRecords: [],
        clientVisits: []
    });

    const customerParams = useMemo(() => {
        // Show all available customers of the particular supplier, even for sales persons
        return {};
    }, []);

    const { customers, loading: customersLoading } = useCustomer(customerParams);

    const handleClientNameChange = (type, idx, value) => {
        updateRow(type, idx, 'clientName', value);
        
        // Auto-fill mobile number if matching customer found
        const customer = (customers || []).find(c => c.name?.toLowerCase() === value?.toLowerCase());
        if (customer && type === 'clientVisits') {
            updateRow(type, idx, 'customerMobileNumber', customer.mobileNumber || '');
        }
    };

    useEffect(() => {
        if (view === 'list') {
            fetchReports();
        }
    }, [view, dateRange, staffFilter, searchQuery]);

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const data = await getSalesPersons();
                setStaffList(data || []);
                
                // If creating a new report (no currentReport) and salesPersonPublicId matches a salesperson in the list
                // with a vehicleNumber, auto-populate it
                if (!currentReport && user?.publicId) {
                    const matched = (data || []).find(s => s.publicId === user.publicId);
                    if (matched && matched.vehicleNumber) {
                        setFormData(prev => ({
                            ...prev,
                            vehicleNo: prev.vehicleNo || matched.vehicleNumber
                        }));
                    }
                }
            } catch (err) {
                console.error('Error fetching staff:', err);
            }
        };
        fetchStaff();
    }, [currentReport, user]);

    const fetchReports = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getDailySalesReports({
                startDate: dateRange.start || null,
                endDate: dateRange.end || null,
                salesPersonPublicId: isSalesPersonOnly ? user?.publicId : (staffFilter || null)
            });
            setReports(data || []);

            // Pre-populate detailedData cache from fetched reports list to prevent lazy-loading API storms on mobile
            if (data && data.length > 0) {
                const cache = {};
                data.forEach(r => {
                    cache[r.publicId] = r;
                });
                setDetailedData(prev => ({ ...prev, ...cache }));
            }
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError("Failed to load reports. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setCurrentReport(null);
        const currentUserSalesperson = (staffList || []).find(s => s.publicId === (user?.publicId || ''));
        const defaultVehicleNo = currentUserSalesperson?.vehicleNumber || '';

        setFormData({
            reportDate: format(new Date(), 'yyyy-MM-dd'),
            salesPersonPublicId: user?.publicId || '',
            helperName: '',
            vehicleNo: defaultVehicleNo,
            odometerStart: '',
            odometerEnd: '',
            openingCash: '',
            salesRecords: [{ clientName: '', invoiceNo: '', cashAmount: '', creditAmount: '', b2bCashAmount: '', b2bBankAmount: '', bankAmount: '' }],
            expenseRecords: [{ description: '', amount: '' }],
            clientVisits: [{ clientName: '', customerMobileNumber: '', visitType: 'In Person Visit', outcomeNotes: '', notes: '', followUpRequired: false, followUpDetails: '' }]
        });
        setIsDirty(false); // Reset dirty flag
        setExistingReportForDate(null);
        setView('form');
    };

    const updateBasicField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const checkDuplicateReport = async (date, staffId) => {
        if (!date || !staffId) return;
        
        // Skip check if we are editing and the date/staff match the current report
        if (currentReport && currentReport.reportDate === date && currentReport.salesPersonPublicId === staffId) {
            setExistingReportForDate(null);
            return;
        }

        try {
            const list = await getDailySalesReports({
                startDate: date,
                endDate: date,
                salesPersonPublicId: staffId
            });
            
            // Filter out the current report if we are editing
            const duplicate = (list || []).find(r => r.salesPersonPublicId === staffId && r.publicId !== currentReport?.publicId);
            
            if (duplicate) {
                setExistingReportForDate(duplicate);
                setError(`A daily ledger already exists for ${safeFormatDate(date, 'MMMM dd, yyyy')} by this staff.`);
            } else {
                setExistingReportForDate(null);
                // Clear the duplicate error if it was set
                setError(prev => prev?.includes("already exists") ? null : prev);
            }
        } catch (err) {
            console.error("Error checking duplicate report:", err);
        }
    };

    useEffect(() => {
        if (view === 'form') {
            const isEditingSameDate = currentReport && currentReport.reportDate === formData.reportDate && currentReport.salesPersonPublicId === formData.salesPersonPublicId;
            if (isEditingSameDate) {
                setExistingReportForDate(null);
                setError(prev => prev?.includes("already exists") ? null : prev);
            } else {
                checkDuplicateReport(formData.reportDate, formData.salesPersonPublicId);
            }
        } else {
            setExistingReportForDate(null);
        }
    }, [formData.reportDate, formData.salesPersonPublicId, view, currentReport]);

    const toggleExpand = async (reportId) => {
        if (expandedReportId === reportId) {
            setExpandedReportId(null);
            return;
        }

        setExpandedReportId(reportId);
        if (!detailedData[reportId]) {
            try {
                const fullData = await getDailySalesReport(reportId);
                setDetailedData(prev => ({ ...prev, [reportId]: fullData }));
            } catch (err) {
                console.error('Error fetching report details:', err);
            }
        }
    };

    const addRow = (type) => {
        const newRow = type === 'salesRecords'
            ? { clientName: '', invoiceNo: '', cashAmount: '', creditAmount: '', b2bCashAmount: '', b2bBankAmount: '', bankAmount: '' }
            : type === 'expenseRecords'
                ? { description: '', amount: '' }
                : { clientName: '', customerMobileNumber: '', visitType: 'In Person Visit', outcomeNotes: '', notes: '', followUpRequired: false, followUpDetails: '' };

        setFormData(prev => ({
            ...prev,
            [type]: [...(prev[type] || []), newRow]
        }));
        setIsDirty(true);
    };

    const removeRow = (type, index) => {
        setFormData(prev => ({
            ...prev,
            [type]: (prev[type] || []).filter((_, i) => i !== index)
        }));
        setIsDirty(true);
    };

    const updateRow = (type, index, field, value) => {
        setFormData(prev => {
            const newList = [...(prev[type] || [])];
            newList[index] = { ...newList[index], [field]: value };
            return { ...prev, [type]: newList };
        });
        setIsDirty(true);
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        if (existingReportForDate) {
            setError("A daily ledger already exists for this date. Please edit the existing entry.");
            return;
        }

        try {
            setIsSaving(true);
            setError(null);
            
            // Sanitize data: filter out empty records
            const sanitizedData = {
                ...formData,
                salesRecords: (formData.salesRecords || []).filter(s => s.clientName || s.invoiceNo || s.cashAmount || s.bankAmount),
                expenseRecords: (formData.expenseRecords || []).filter(e => e.description || e.amount),
                clientVisits: (formData.clientVisits || []).filter(v => v.clientName)
            };

            const response = await createDailySalesReport(sanitizedData);
            
            // Fetch the full detailed report from query side to properly populate the cache and form state
            const fullReport = await getDailySalesReport(response.publicId);
            
            // Update current report with saved data so it switches to "Edit" mode natively
            setCurrentReport(fullReport);
            setIsDirty(false); // Saved successfully, clear dirty state!
            setExistingReportForDate(null); // Clear duplicate ledger on successful save
            
            // Update detailedData cache so the list/details immediately reflect the new data!
            setDetailedData(prev => ({
                ...prev,
                [response.publicId]: fullReport
            }));

            // Show success message and auto-hide after 5 seconds
            setSuccessMessage("Log saved successfully!");
            setTimeout(() => setSuccessMessage(null), 5000);
            
        } catch (err) {
            console.error('Error saving report:', err);
            const msg = handleApiError(err);
            setError(msg);
            // alert(msg); // Removed in favor of on-page header
        } finally {
            setIsSaving(false);
        }
    };

    const renderListView = () => (
        <div className="flex flex-col h-full bg-gray-50/50">
            {/* Header */}
            <div className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-30 shadow-sm">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                        <div className="p-2 bg-blue-600 rounded-xl text-white shadow-lg shadow-blue-200">
                            <ClipboardList size={20} />
                        </div>
                        Operations Log
                    </h1>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1 ml-11">Daily Activity Ledger</p>
                </div>
                <div className="flex items-center gap-3">
                    {reports.length > 0 && (
                        <div className="relative">
                            <button
                                onClick={() => {
                                    if (isManagement) {
                                        setShowPdfMenu(prev => !prev);
                                    } else {
                                        downloadConsolidatedPdf(dateRange.start, dateRange.end, isSalesPersonOnly ? user?.publicId : staffFilter, false);
                                    }
                                }}
                                className="p-2.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all border border-gray-100 bg-white shadow-sm flex items-center justify-center active:scale-95"
                                title="Export Consolidated PDF"
                            >
                                <Download size={20} />
                            </button>
                            
                            {showPdfMenu && (
                                <>
                                    {/* Backdrop */}
                                    <div className="fixed inset-0 z-40" onClick={() => setShowPdfMenu(false)} />
                                    
                                    {/* Dropdown list */}
                                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 py-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                                        <button
                                            onClick={() => {
                                                setShowPdfMenu(false);
                                                downloadConsolidatedPdf(dateRange.start, dateRange.end, isSalesPersonOnly ? user?.publicId : staffFilter, false);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-xs font-bold text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center gap-2"
                                        >
                                            <Users size={14} /> Include All Staff
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowPdfMenu(false);
                                                downloadConsolidatedPdf(dateRange.start, dateRange.end, isSalesPersonOnly ? user?.publicId : staffFilter, true);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-xs font-bold text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center gap-2"
                                        >
                                            <User size={14} /> Only Sales Staff
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <button
                        onClick={handleCreateNew}
                        className="bg-gray-900 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-black text-xs shadow-xl hover:bg-black transition-all active:scale-95 uppercase tracking-wider"
                    >
                        <Plus size={16} /> New Entry
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
                {/* 1. Filters Bar */}
                <div className="flex flex-col md:flex-row gap-4">
                    {!isSalesPersonOnly && (
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by staff, vehicle, or helper..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500/20 text-sm font-medium shadow-sm transition-all"
                            />
                        </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                        <CustomDatePicker
                            mode="range"
                            startDate={dateRange.start}
                            endDate={dateRange.end}
                            onStartChange={(val) => setDateRange(prev => ({ ...prev, start: val }))}
                            onEndChange={(val) => setDateRange(prev => ({ ...prev, end: val }))}
                        />

                        {/* Quick Date Buttons */}
                        <div className="flex gap-1 bg-white p-1 rounded-xl border border-gray-100 shadow-sm">
                            {[
                                { id: 'today', label: 'Today', sub: format(new Date(), 'dd MMM') },
                                { id: 'yesterday', label: 'Yesterday', sub: format(new Date(Date.now() - 86400000), 'dd MMM') },
                                { id: 'lastWeek', label: 'Last Week', sub: '7 Days' }
                            ].map(btn => {
                                const isToday = btn.id === 'today' && dateRange.start === format(new Date(), 'yyyy-MM-dd') && dateRange.end === format(new Date(), 'yyyy-MM-dd');
                                const isYesterday = btn.id === 'yesterday' && dateRange.start === format(new Date(Date.now() - 86400000), 'yyyy-MM-dd') && dateRange.end === format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');
                                const isLastWeek = btn.id === 'lastWeek' && dateRange.start === format(new Date(Date.now() - 7 * 86400000), 'yyyy-MM-dd') && dateRange.end === format(new Date(), 'yyyy-MM-dd');
                                const isActive = isToday || isYesterday || isLastWeek;
                                
                                return (
                                <button
                                    key={btn.id}
                                    onClick={() => {
                                        const now = new Date();
                                        if (btn.id === 'today') setDateRange({ start: format(now, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') });
                                        else if (btn.id === 'yesterday') {
                                            const y = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');
                                            setDateRange({ start: y, end: y });
                                        }
                                        else if (btn.id === 'lastWeek') {
                                            setDateRange({ 
                                                start: format(new Date(Date.now() - 7 * 86400000), 'yyyy-MM-dd'), 
                                                end: format(now, 'yyyy-MM-dd') 
                                            });
                                        }
                                    }}
                                    className={`px-3 py-1.5 rounded-lg flex flex-col items-center transition-all ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'text-gray-500 hover:bg-gray-50'}`}
                                >
                                    <span className="text-[9px] font-black uppercase tracking-tighter leading-none">{btn.label}</span>
                                    <span className={`text-[8px] font-bold mt-0.5 ${isActive ? 'text-blue-100' : 'text-gray-400'}`}>{btn.sub}</span>
                                </button>
                                );
                            })}
                        </div>

                        <div className="flex gap-2 flex-1 md:flex-initial items-center w-full md:w-auto">
                            {!isSalesPersonOnly && isManagement && (
                                <select
                                    value={staffFilter}
                                    onChange={(e) => setStaffFilter(e.target.value)}
                                    className="flex-1 md:w-48 px-4 py-3 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500/20 text-xs font-black uppercase tracking-widest shadow-sm"
                                >
                                    <option value="">All Staff</option>
                                    {(staffList || []).map(s => (
                                        <option key={s.publicId} value={s.publicId}>{s.name}</option>
                                    ))}
                                </select>
                            )}
                            {/* Clear Filters Button */}
                            {(dateRange.start || dateRange.end || staffFilter || searchQuery) && (
                                <button
                                    onClick={() => { setDateRange({start: '', end: ''}); setStaffFilter(''); setSearchQuery(''); }}
                                    className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors border border-red-100 bg-white shadow-sm flex items-center justify-center shrink-0"
                                    title="Clear Filters"
                                >
                                    <X size={20} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-100 p-4 rounded-2xl flex items-center gap-3 text-red-600">
                        <AlertCircle size={20} />
                        <p className="text-sm font-bold">{error}</p>
                        <button onClick={fetchReports} className="ml-auto text-xs font-black uppercase underline">Retry</button>
                    </div>
                )}

                {successMessage && (
                    <div className="bg-green-50 border border-green-100 p-4 rounded-2xl flex items-center gap-3 text-green-600 animate-in slide-in-from-top duration-300">
                        <CheckCircle2 size={20} />
                        <p className="text-sm font-bold">{successMessage}</p>
                        <button onClick={() => setSuccessMessage(null)} className="ml-auto text-xs font-black uppercase underline">Dismiss</button>
                    </div>
                )}

                {/* 2. Quick Glance Dashboard */}
                {!loading && reports.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {/* Gross Sales */}
                        {(() => {
                            const valStr = `AED ${(Number(dailySummary.totalSales) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            const len = valStr.length;
                            const textSizeClass = len > 16 ? 'text-sm sm:text-base' : len > 13 ? 'text-base sm:text-lg' : 'text-lg sm:text-xl';
                            return (
                                <div className="bg-[var(--color-bg-card)] p-4 rounded-2xl border border-[var(--color-border)] shadow-xs flex flex-col justify-between group hover:border-blue-300 transition-colors min-w-0">
                                    <div className="flex items-center justify-between mb-2 gap-2">
                                        <div className="p-2 bg-blue-50 dark:bg-blue-950/40 rounded-xl text-blue-600 dark:text-blue-400 shrink-0">
                                            <ShoppingBag size={18} />
                                        </div>
                                        <span className="text-[10px] font-black text-[var(--color-text-muted)] uppercase tracking-widest truncate">Gross Sales</span>
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`${textSizeClass} font-black text-[var(--color-text-primary)] tracking-tight break-words`} title={valStr}>
                                            {valStr}
                                        </p>
                                        <div className="flex items-center gap-1 mt-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                            <p className="text-[9px] font-bold text-[var(--color-text-muted)] uppercase">Live Aggregation</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Net Cash */}
                        {(() => {
                            const valStr = `AED ${(Number(dailySummary.totalCash) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            const len = valStr.length;
                            const textSizeClass = len > 16 ? 'text-sm sm:text-base' : len > 13 ? 'text-base sm:text-lg' : 'text-lg sm:text-xl';
                            return (
                                <div className="bg-[var(--color-bg-card)] p-4 rounded-2xl border border-[var(--color-border)] shadow-xs flex flex-col justify-between group hover:border-emerald-300 transition-colors min-w-0">
                                    <div className="flex items-center justify-between mb-2 gap-2">
                                        <div className="p-2 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl text-emerald-600 dark:text-emerald-400 shrink-0">
                                            <Wallet size={18} />
                                        </div>
                                        <span className="text-[10px] font-black text-[var(--color-text-muted)] uppercase tracking-widest truncate">Net Cash</span>
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`${textSizeClass} font-black text-[var(--color-text-primary)] tracking-tight break-words`} title={valStr}>
                                            {valStr}
                                        </p>
                                        <p className="text-[9px] font-bold text-[var(--color-text-muted)] uppercase mt-1 italic">Incl. B2B Collections</p>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Total Expenses */}
                        {(() => {
                            const valStr = `AED ${(Number(dailySummary.totalExpenses) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            const len = valStr.length;
                            const textSizeClass = len > 16 ? 'text-sm sm:text-base' : len > 13 ? 'text-base sm:text-lg' : 'text-lg sm:text-xl';
                            return (
                                <div className="bg-[var(--color-bg-card)] p-4 rounded-2xl border border-[var(--color-border)] shadow-xs flex flex-col justify-between group hover:border-rose-300 transition-colors min-w-0">
                                    <div className="flex items-center justify-between mb-2 gap-2">
                                        <div className="p-2 bg-rose-50 dark:bg-rose-950/40 rounded-xl text-rose-600 dark:text-rose-400 shrink-0">
                                            <CreditCard size={18} />
                                        </div>
                                        <span className="text-[10px] font-black text-[var(--color-text-muted)] uppercase tracking-widest truncate">Total Exp.</span>
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`${textSizeClass} font-black text-rose-600 dark:text-rose-400 tracking-tight break-words`} title={valStr}>
                                            {valStr}
                                        </p>
                                        <p className="text-[9px] font-bold text-[var(--color-text-muted)] uppercase mt-1">Logistics & Fuel</p>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Net Position */}
                        {(() => {
                            const valStr = `AED ${(Number(dailySummary.netPosition) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            const len = valStr.length;
                            const textSizeClass = len > 16 ? 'text-sm sm:text-base' : len > 13 ? 'text-base sm:text-lg' : 'text-lg sm:text-xl';
                            return (
                                <div className="bg-gradient-to-br from-gray-900 to-black text-white p-4 rounded-2xl border border-gray-800 shadow-xl flex flex-col justify-between min-w-0">
                                    <div className="flex items-center justify-between mb-2 gap-2">
                                        <div className="p-2 bg-white/10 rounded-xl text-blue-400 shrink-0">
                                            <LayoutList size={18} />
                                        </div>
                                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">Net Position</span>
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`${textSizeClass} font-black tracking-tight break-words`} title={valStr}>
                                            {valStr}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <div className="px-1.5 py-0.5 bg-blue-500 rounded text-[8px] font-black uppercase">
                                                {Number(dailySummary.totalVisits) || 0} Visits
                                            </div>
                                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">EOD Liquid Goal</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* 3. Activity Listing */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between px-2">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                            {isSingleDay ? "Consolidated Daily Operations" : "Staff Activity History"}
                        </h3>
                        {!loading && reports.length > 0 && (
                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full uppercase">
                                {filteredReports.length} Reports Found
                            </span>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
                            <LoadingSpinner size="lg" />
                            <p className="mt-4 text-gray-500 font-bold uppercase text-[10px] tracking-widest">Processing Data...</p>
                        </div>
                    ) : reports.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-dashed border-gray-200">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                                <FileText size={36} className="text-gray-300" />
                            </div>
                            <h3 className="text-gray-900 font-black text-lg uppercase tracking-tighter">No activity logged</h3>
                            <p className="text-gray-500 text-sm mt-1 font-medium">Select a different date or start a new ledger entry.</p>
                            <button
                                onClick={handleCreateNew}
                                className="mt-8 px-8 py-3 bg-blue-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-blue-700 transition-all active:scale-95"
                            >
                                Create Today's Entry
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Render Single Day View (Fully Expanded) */}
                            {isSingleDay ? (
                                <div className="space-y-8">
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50/20 p-5 rounded-2xl border border-blue-100/50 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                                        <div>
                                            <p className="text-[8px] font-black text-blue-500 uppercase tracking-[0.2em] mb-1">Single Day Selected</p>
                                            <h2 className="text-xl font-black text-gray-900 tracking-tight">{safeFormatDate(dateRange.start, 'EEEE, MMMM dd, yyyy')}</h2>
                                        </div>
                                        {!isSalesPersonOnly && (
                                            <div className="relative">
                                                <button
                                                    onClick={() => {
                                                        if (isManagement) {
                                                            setShowPdfMenuList(prev => !prev);
                                                        } else {
                                                            downloadConsolidatedPdf(dateRange.start, dateRange.end, isSalesPersonOnly ? user?.publicId : staffFilter, false);
                                                        }
                                                    }}
                                                    className="self-start sm:self-center px-4 py-2 bg-blue-600 text-white font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-blue-700 transition-all active:scale-95 flex items-center gap-1.5 shadow-lg shadow-blue-100"
                                                >
                                                    <Download size={14} /> Export Day's PDF
                                                </button>
                                                
                                                {showPdfMenuList && (
                                                    <>
                                                        {/* Backdrop */}
                                                        <div className="fixed inset-0 z-40" onClick={() => setShowPdfMenuList(false)} />
                                                        
                                                        {/* Dropdown list */}
                                                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 py-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                                                            <button
                                                                onClick={() => {
                                                                    setShowPdfMenuList(false);
                                                                    downloadConsolidatedPdf(dateRange.start, dateRange.end, isSalesPersonOnly ? user?.publicId : staffFilter, false);
                                                                }}
                                                                className="w-full text-left px-4 py-2.5 text-xs font-bold text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center gap-2"
                                                            >
                                                                <Users size={14} /> Include All Staff
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setShowPdfMenuList(false);
                                                                    downloadConsolidatedPdf(dateRange.start, dateRange.end, isSalesPersonOnly ? user?.publicId : staffFilter, true);
                                                                }}
                                                                className="w-full text-left px-4 py-2.5 text-xs font-bold text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center gap-2"
                                                            >
                                                                <User size={14} /> Only Sales Staff
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-6">
                                        {filteredReports.map(report => {
                                            const isReportExpanded = isSalesPersonOnly || !!expandedReports[report.publicId];
                                            return (
                                                <div
                                                    key={report.publicId}
                                                    className={`bg-white rounded-3xl border transition-all overflow-hidden ${
                                                        isReportExpanded ? 'ring-2 ring-blue-500/10 border-blue-200 shadow-md' : 'border-gray-100 shadow-sm hover:border-gray-200'
                                                    }`}
                                                >
                                                    {/* Accordion Card Header */}
                                                    <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/20">
                                                        <div
                                                            className={`flex items-center gap-3 flex-1 ${isSalesPersonOnly ? '' : 'cursor-pointer'}`}
                                                            onClick={isSalesPersonOnly ? undefined : () => toggleReportExpand(report.publicId)}
                                                        >
                                                            <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-lg shrink-0">
                                                                {report.salesPersonName?.charAt(0) || 'S'}
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <h3 className="font-black text-gray-900 tracking-tight text-base">{report.salesPersonName}</h3>
                                                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-bold rounded-full uppercase">
                                                                        Open: AED {report.openingCash || 0}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-3 mt-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                                                    <span className="flex items-center gap-1"><Hash size={10} className="text-gray-300" /> {report.vehicleNo || 'NO VEHICLE'}</span>
                                                                    {report.helperName && <span>• Helper: {report.helperName}</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="flex items-center gap-3 justify-between sm:justify-end">
                                                            <button
                                                                onClick={() => handleViewReport(report)}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-[9px] font-black uppercase tracking-widest transition-colors"
                                                            >
                                                                <Eye size={12} /> View
                                                            </button>
                                                            {report.salesPersonPublicId === user?.publicId && (
                                                                <button
                                                                    onClick={() => handleEditReport(report)}
                                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-[9px] font-black uppercase tracking-widest transition-colors"
                                                                >
                                                                    <Edit2 size={12} /> Edit
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => downloadConsolidatedPdf(report.reportDate, report.reportDate, report.salesPersonPublicId)}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-[9px] font-black uppercase tracking-widest transition-colors"
                                                            >
                                                                <Download size={12} /> PDF
                                                            </button>
                                                            {!isSalesPersonOnly && (
                                                                <button
                                                                    onClick={() => toggleReportExpand(report.publicId)}
                                                                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                                                        isReportExpanded ? 'bg-blue-500 text-white rotate-180' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                                                    }`}
                                                                >
                                                                    <ChevronLeft size={16} className="-rotate-90" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Collapsed Content */}
                                                    {isReportExpanded && (
                                                        <div className="p-6 border-t border-gray-100 bg-white space-y-6">
                                                            <StaffReportDetailCard
                                                                report={report}
                                                                detailedData={detailedData}
                                                                setDetailedData={setDetailedData}
                                                                handleEditReport={handleEditReport}
                                                                handleViewReport={handleViewReport}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                /* Render Multi-Day View (Collapsible Cards) */
                                <div className="space-y-8">
                                    {isSalesPersonOnly ? (
                                        /* Case 1: Salesperson - Grouped by Date */
                                        <div className="space-y-4">
                                            {groupedReports.map(group => {
                                                const dateStr = group.key;
                                                const isExpanded = expandedReports[dateStr];
                                                const dayReports = group.reports || [];
                                                const report = dayReports[0];
                                                
                                                let salesVal = 0;
                                                dayReports.forEach(r => {
                                                    salesVal += Number(r.totalSaleSummary) || 0;
                                                });

                                                return (
                                                    <div
                                                        key={dateStr}
                                                        className={`bg-white rounded-3xl border transition-all overflow-hidden ${
                                                            isExpanded ? 'ring-2 ring-blue-500/10 border-blue-200 shadow-md' : 'border-gray-100 shadow-sm hover:border-gray-200'
                                                        }`}
                                                    >
                                                        {/* Accordion Card Header */}
                                                        <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50/20">
                                                            <div
                                                                className="flex items-center gap-3 cursor-pointer flex-1"
                                                                onClick={() => toggleReportExpand(dateStr)}
                                                            >
                                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                                                                    isExpanded ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                                                                }`}>
                                                                    <Calendar size={18} />
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-black text-gray-900 tracking-tight">{group.label}</h4>
                                                                    <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">
                                                                        {dayReports.length} {dayReports.length === 1 ? 'Report' : 'Reports'}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center gap-6 justify-between md:justify-end">
                                                                <div className="text-right border-r border-gray-100 pr-6">
                                                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Sales</p>
                                                                    <span className="text-base font-black text-gray-900">AED {salesVal.toLocaleString()}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    {report && (
                                                                        <>
                                                                            <button
                                                                                onClick={() => handleViewReport(report)}
                                                                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                                                                title="View Report"
                                                                            >
                                                                                <Eye size={16} />
                                                                            </button>
                                                                            {report.salesPersonPublicId === user?.publicId && (
                                                                                <button
                                                                                    onClick={() => handleEditReport(report)}
                                                                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                                                                    title="Edit Entry"
                                                                                >
                                                                                    <Edit2 size={16} />
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    <button
                                                                        onClick={() => downloadConsolidatedPdf(dateStr, dateStr, user?.publicId)}
                                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                                                        title="Export Day's PDF"
                                                                    >
                                                                        <Download size={16} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => toggleReportExpand(dateStr)}
                                                                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                                                            isExpanded ? 'bg-blue-500 text-white rotate-180' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                                                        }`}
                                                                    >
                                                                        <ChevronLeft size={16} className="-rotate-90" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Expanded Content */}
                                                        {isExpanded && (
                                                            <div className="p-6 border-t border-gray-100 bg-white space-y-6">
                                                                {dayReports.map(report => (
                                                                    <StaffReportDetailCard
                                                                        key={report.publicId}
                                                                        report={report}
                                                                        detailedData={detailedData}
                                                                        setDetailedData={setDetailedData}
                                                                        handleEditReport={handleEditReport}
                                                                        handleViewReport={handleViewReport}
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        /* Case 2: Managers / Admin - Grouped by Staff first, then Date */
                                        <div className="space-y-8">
                                            {groupedReports.map(staffGroup => {
                                                const staffId = staffGroup.key;
                                                const isStaffExpanded = !!expandedStaff[staffId]; // default collapsed
                                                const reportsList = staffGroup.reports || [];
                                                
                                                // Calculate staff metrics for this range
                                                let salesVal = 0, expVal = 0, netVal = 0;
                                                reportsList.forEach(r => {
                                                    salesVal += Number(r.totalSaleSummary) || 0;
                                                    expVal += Number(r.totalExpenses) || 0;
                                                    netVal += Number(r.finalNetPosition) || 0;
                                                });

                                                return (
                                                    <div key={staffId} className="space-y-4">
                                                        {/* Staff Header Section */}
                                                        <div className="flex items-center justify-between bg-white px-6 py-4 rounded-2xl border border-gray-100 shadow-sm">
                                                            <div
                                                                className="flex items-center gap-3 cursor-pointer"
                                                                onClick={() => toggleStaffExpand(staffId)}
                                                            >
                                                                <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center font-black">
                                                                    {staffGroup.name?.charAt(0) || 'S'}
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-black text-gray-900 tracking-tight">{staffGroup.name}</h4>
                                                                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                                                                        {reportsList.length} active {reportsList.length === 1 ? 'day' : 'days'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="flex items-center gap-6">
                                                                <div className="hidden sm:block text-right border-r border-gray-100 pr-6">
                                                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Gross Sales</p>
                                                                    <span className="text-sm font-black text-gray-900">AED {salesVal.toLocaleString()}</span>
                                                                </div>
                                                                <div className="hidden sm:block text-right border-r border-gray-100 pr-6">
                                                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Expenses</p>
                                                                    <span className="text-sm font-black text-red-600">AED {expVal.toLocaleString()}</span>
                                                                </div>
                                                                <div className="text-right sm:pr-4">
                                                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Net Pos</p>
                                                                    <span className="text-sm font-black text-blue-600">AED {netVal.toLocaleString()}</span>
                                                                </div>
                                                                <button
                                                                    onClick={() => toggleStaffExpand(staffId)}
                                                                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all bg-gray-50 text-gray-400 ${
                                                                        isStaffExpanded ? 'rotate-180 bg-gray-100 text-gray-600' : ''
                                                                    }`}
                                                                >
                                                                    <ChevronLeft size={16} className="-rotate-90" />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Staff Dates Collapsible List */}
                                                        {isStaffExpanded && (
                                                            <div className="pl-6 space-y-3 border-l-2 border-gray-100 ml-5">
                                                                {reportsList.map(report => {
                                                                    const isReportExpanded = expandedReports[report.publicId];
                                                                    return (
                                                                        <div
                                                                            key={report.publicId}
                                                                            className={`bg-white rounded-2xl border transition-all overflow-hidden ${
                                                                                isReportExpanded ? 'ring-2 ring-blue-500/10 border-blue-200 shadow-md' : 'border-gray-100 shadow-sm hover:border-gray-200'
                                                                            }`}
                                                                        >
                                                                            {/* Date Card Header */}
                                                                            <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50/10">
                                                                                <div
                                                                                    className="flex items-center gap-3 cursor-pointer flex-1"
                                                                                    onClick={() => toggleReportExpand(report.publicId)}
                                                                                >
                                                                                    <div className="text-xs font-black text-gray-700 bg-gray-100 px-2.5 py-1 rounded-lg">
                                                                                        {safeFormatDate(report.reportDate, 'dd MMM yyyy')}
                                                                                    </div>
                                                                                    <div className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-2">
                                                                                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{report.vehicleNo || 'No vehicle'}</span>
                                                                                        {report.helperName && <span>• Helper: {report.helperName}</span>}
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                <div className="flex items-center gap-4 justify-between sm:justify-end">
                                                                                    <div className="text-right">
                                                                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Sales:</span>
                                                                                        <span className="text-sm font-black text-gray-900">AED {(Number(report.totalSaleSummary) || 0).toLocaleString()}</span>
                                                                                    </div>
                                                                                    
                                                                                    <div className="flex items-center gap-2">
                                                                                        <button
                                                                                            onClick={() => handleViewReport(report)}
                                                                                            className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-[9px] font-black uppercase tracking-widest transition-colors"
                                                                                            title="View Details"
                                                                                        >
                                                                                            <Eye size={12} />
                                                                                        </button>
                                                                                        {report.salesPersonPublicId === user?.publicId && (
                                                                                            <button
                                                                                                onClick={() => handleEditReport(report)}
                                                                                                className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-[9px] font-black uppercase tracking-widest transition-colors"
                                                                                                title="Edit Entry"
                                                                                            >
                                                                                                <Edit2 size={12} />
                                                                                            </button>
                                                                                        )}
                                                                                        <button
                                                                                            onClick={() => downloadConsolidatedPdf(report.reportDate, report.reportDate, report.salesPersonPublicId)}
                                                                                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                                                                            title="Download PDF"
                                                                                        >
                                                                                            <Download size={14} />
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => toggleReportExpand(report.publicId)}
                                                                                            className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                                                                                                isReportExpanded ? 'bg-blue-500 text-white rotate-180' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                                                                                            }`}
                                                                                        >
                                                                                            <ChevronLeft size={14} className="-rotate-90" />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            {/* Date Card Expanded Details */}
                                                                            {isReportExpanded && (
                                                                                <div className="p-5 border-t border-gray-100 bg-white">
                                                                                    <StaffReportDetailCard
                                                                                        report={report}
                                                                                        detailedData={detailedData}
                                                                                        setDetailedData={setDetailedData}
                                                                                        handleEditReport={handleEditReport}
                                                                                        handleViewReport={handleViewReport}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

        </div>
    );

    const renderFormView = () => (
        <div className="flex flex-col h-full bg-gray-50/50">
            {/* Header */}
            <div className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-30 shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => {
                            if (isDirty) {
                                setShowConfirmBack(true);
                            } else {
                                setView('list');
                            }
                        }}
                        className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-xl font-black text-gray-900 tracking-tight">
                            {currentReport ? 'Edit Entry' : 'New Daily Ledger'}
                        </h1>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Step 1: Activity & Financial Log</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving || !!existingReportForDate}
                        className={`bg-blue-600 text-white px-8 py-2.5 rounded-xl flex items-center gap-2 font-black text-xs shadow-xl hover:bg-blue-700 transition-all uppercase tracking-wider disabled:opacity-50 ${isSaving || !!existingReportForDate ? '' : 'active:scale-95'}`}
                    >
                        {isSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                        Save
                    </button>
                </div>
            </div>

            {/* Error Header */}
            {error && (
                <div className="bg-red-50 border-b border-red-100 px-6 py-3 flex items-center justify-between sticky top-[73px] z-20 animate-in slide-in-from-top duration-300">
                    <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-red-100 text-red-600 rounded-lg">
                            <AlertCircle size={16} />
                        </div>
                        <p className="text-xs font-bold text-red-800">{error}</p>
                    </div>
                    <button 
                        onClick={() => setError(null)}
                        className="p-1 hover:bg-red-100 rounded-md text-red-400 hover:text-red-600 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Success Header */}
            {successMessage && (
                <div className="bg-green-50 border-b border-green-100 px-6 py-3 flex items-center justify-between sticky top-[73px] z-20 animate-in slide-in-from-top duration-300">
                    <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-green-100 text-green-600 rounded-lg">
                            <CheckCircle2 size={16} />
                        </div>
                        <p className="text-xs font-bold text-green-800">{successMessage}</p>
                    </div>
                    <button 
                        onClick={() => setSuccessMessage(null)}
                        className="p-1 hover:bg-green-100 rounded-md text-green-400 hover:text-green-600 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-5xl mx-auto space-y-8 pb-12">
                    {/* Duplicate Ledger Detected Alert */}
                    {existingReportForDate && (
                        <div className="bg-amber-50 border border-amber-100 p-5 rounded-3xl flex items-start justify-between gap-4 animate-in slide-in-from-top-4 duration-300 shadow-sm">
                            <div className="flex gap-3">
                                <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
                                    <AlertCircle size={20} />
                                </div>
                                <div>
                                    <h4 className="text-xs font-black text-amber-900 uppercase tracking-wide">Duplicate Ledger Detected</h4>
                                    <p className="text-[10px] font-bold text-amber-600 mt-1 leading-relaxed">
                                        A daily log is already saved for this staff on this date. You cannot save duplicate entries.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleEditReport(existingReportForDate)}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-wider transition-all shadow-lg shadow-amber-100 flex-shrink-0"
                            >
                                Edit Existing Log
                            </button>
                        </div>
                    )}

                    {/* Basic Info Card */}
                    <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-8">
                        <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                                <Info size={18} />
                            </div>
                            <h3 className="font-black text-gray-900 uppercase tracking-widest text-xs">Route & Vehicle Information</h3>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="md:col-span-1">
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Entry Date</label>
                                <CustomDatePicker
                                    mode="single"
                                    value={formData.reportDate}
                                    onChange={(val) => updateBasicField('reportDate', val)}
                                    quickFilters={false}
                                    className="w-full"
                                />
                            </div>

                            <div className="md:col-span-1">
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Primary Staff</label>
                                <div className="relative">
                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <select
                                        value={formData.salesPersonPublicId}
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            updateBasicField('salesPersonPublicId', selectedId);
                                            
                                            // Auto-populate vehicle number if available
                                            const selectedStaff = (staffList || []).find(s => s.publicId === selectedId);
                                            if (selectedStaff && selectedStaff.vehicleNumber) {
                                                updateBasicField('vehicleNo', selectedStaff.vehicleNumber);
                                            }
                                        }}
                                        className="w-full pl-11 pr-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-sm appearance-none"
                                        disabled={isSalesPersonOnly}
                                    >
                                        <option value="">Select Staff</option>
                                        {(staffList || []).map(s => (
                                            <option key={s.publicId} value={s.publicId}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="md:col-span-1">
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Vehicle Plate</label>
                                <div className="relative">
                                    <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        value={formData.vehicleNo}
                                        onChange={(e) => updateBasicField('vehicleNo', e.target.value)}
                                        className="w-full pl-11 pr-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-sm"
                                        placeholder="Plate No."
                                    />
                                </div>
                            </div>

                            <div className="md:col-span-1">
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Assistant / Helper</label>
                                <div className="relative">
                                    <UserPlus className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        value={formData.helperName}
                                        onChange={(e) => updateBasicField('helperName', e.target.value)}
                                        className="w-full pl-11 pr-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-sm"
                                        placeholder="Name"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3 md:gap-6 pt-4 border-t border-gray-50">
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Odometer Start (KM)</label>
                                <input
                                    type="number"
                                    value={formData.odometerStart}
                                    onChange={(e) => updateBasicField('odometerStart', e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-sm"
                                    placeholder="00000"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Odometer End (KM)</label>
                                <input
                                    type="number"
                                    value={formData.odometerEnd}
                                    onChange={(e) => updateBasicField('odometerEnd', e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-sm"
                                    placeholder="00000"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Opening Cash Float</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">AED</span>
                                    <input
                                        type="number"
                                        value={formData.openingCash}
                                        onChange={(e) => updateBasicField('openingCash', e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-sm"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sales & Activity Tables */}
                    <div className="space-y-6">
                        {/* 1. Sales Records */}
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between bg-blue-50/30">
                                <h3 className="font-black text-gray-900 uppercase tracking-widest text-xs flex items-center gap-2">
                                    <ShoppingBag size={16} className="text-blue-600" />
                                    Daily Sales Transactions
                                </h3>
                                <button
                                    onClick={() => addRow('salesRecords')}
                                    className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md shadow-blue-100"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            <div className="p-0 overflow-x-auto">
                                <table className="w-full text-sm min-w-[900px]">
                                    <thead>
                                        <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                            <th className="px-3 md:px-6 py-4 text-left min-w-[250px]">Client Name</th>
                                            <th className="px-3 md:px-6 py-4 text-left min-w-[100px]">Inv #</th>
                                            <th className="px-3 md:px-6 py-4 text-right min-w-[100px]">Cash</th>
                                            <th className="px-3 md:px-6 py-4 text-right min-w-[100px]">Credit</th>
                                            <th className="px-3 md:px-6 py-4 text-right min-w-[100px]">B2B Cash</th>
                                            <th className="px-3 md:px-6 py-4 text-right min-w-[100px]">B2B Bank</th>
                                            <th className="px-3 md:px-6 py-4 text-right min-w-[100px]">Bank</th>
                                            <th className="px-3 md:px-6 py-4 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {(formData.salesRecords || []).map((row, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50/30 transition-colors">
                                                <td className="px-3 md:px-6 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <Search size={12} className="text-gray-400 shrink-0" />
                                                        <input
                                                            type="text"
                                                            value={row.clientName}
                                                        onChange={(e) => handleClientNameChange('salesRecords', idx, e.target.value)}
                                                        className="w-full bg-transparent font-bold text-gray-900 outline-none"
                                                        placeholder={customersLoading ? "Loading..." : "Client Name"}
                                                        list="customer-list"
                                                    />
                                                    </div>
                                                </td>
                                                <td className="px-3 md:px-6 py-3">
                                                    <input
                                                        type="text"
                                                        value={row.invoiceNo}
                                                        onChange={(e) => updateRow('salesRecords', idx, 'invoiceNo', e.target.value)}
                                                        className="w-full bg-transparent font-medium text-gray-600 outline-none"
                                                        placeholder="Inv #"
                                                    />
                                                </td>
                                                <td className="px-3 md:px-6 py-3">
                                                    <input
                                                        type="number"
                                                        value={row.cashAmount}
                                                        onChange={(e) => updateRow('salesRecords', idx, 'cashAmount', e.target.value)}
                                                        className="w-full bg-transparent font-black text-gray-900 text-right outline-none"
                                                        placeholder="Cash"
                                                    />
                                                </td>
                                                <td className="px-3 md:px-6 py-3">
                                                    <input
                                                        type="number"
                                                        value={row.creditAmount}
                                                        onChange={(e) => updateRow('salesRecords', idx, 'creditAmount', e.target.value)}
                                                        className="w-full bg-transparent font-black text-gray-900 text-right outline-none"
                                                        placeholder="Credit"
                                                    />
                                                </td>
                                                <td className="px-3 md:px-6 py-3">
                                                    <input
                                                        type="number"
                                                        value={row.b2bCashAmount}
                                                        onChange={(e) => updateRow('salesRecords', idx, 'b2bCashAmount', e.target.value)}
                                                        className="w-full bg-transparent font-black text-blue-600 text-right outline-none"
                                                        placeholder="B2B Cash"
                                                    />
                                                </td>
                                                <td className="px-3 md:px-6 py-3">
                                                    <input
                                                        type="number"
                                                        value={row.b2bBankAmount}
                                                        onChange={(e) => updateRow('salesRecords', idx, 'b2bBankAmount', e.target.value)}
                                                        className="w-full bg-transparent font-black text-purple-600 text-right outline-none"
                                                        placeholder="B2B Bank"
                                                    />
                                                </td>
                                                <td className="px-3 md:px-6 py-3">
                                                    <input
                                                        type="number"
                                                        value={row.bankAmount}
                                                        onChange={(e) => updateRow('salesRecords', idx, 'bankAmount', e.target.value)}
                                                        className="w-full bg-transparent font-black text-gray-900 text-right outline-none"
                                                        placeholder="Bank"
                                                    />
                                                </td>
                                                <td className="px-3 md:px-6 py-3">
                                                    <button onClick={() => removeRow('salesRecords', idx)} className="text-gray-300 hover:text-red-500 transition-colors">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Bottom Add Button */}
                            {(formData.salesRecords || []).length > 0 && (
                                <div className="px-8 py-3 border-t border-gray-50 bg-gray-50/30">
                                    <button
                                        onClick={() => addRow('salesRecords')}
                                        className="w-full py-2.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all flex items-center justify-center gap-1.5"
                                    >
                                        <Plus size={14} /> Add Transaction
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* 2. Expenses Records */}
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between bg-red-50/30">
                                <h3 className="font-black text-gray-900 uppercase tracking-widest text-xs flex items-center gap-2">
                                    <CreditCard size={16} className="text-red-600" />
                                    Daily Expense log
                                </h3>
                                <button
                                    onClick={() => addRow('expenseRecords')}
                                    className="p-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-md shadow-red-100"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            <div className="p-0">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                            <th className="px-8 py-4 text-left">Description / Purpose</th>
                                            <th className="px-8 py-4 text-right w-48">Amount (AED)</th>
                                            <th className="px-8 py-4 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {(formData.expenseRecords || []).map((row, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50/30 transition-colors">
                                                <td className="px-8 py-3">
                                                    <input
                                                        type="text"
                                                        value={row.description}
                                                        onChange={(e) => updateRow('expenseRecords', idx, 'description', e.target.value)}
                                                        className="w-full bg-transparent font-bold text-gray-900 outline-none"
                                                        placeholder="Description / Purpose"
                                                        list="common-expenses"
                                                    />
                                                    <datalist id="common-expenses">
                                                        {COMMON_EXPENSES.map(e => <option key={e} value={e} />)}
                                                    </datalist>
                                                </td>
                                                <td className="px-8 py-3">
                                                    <input
                                                        type="number"
                                                        value={row.amount}
                                                        onChange={(e) => updateRow('expenseRecords', idx, 'amount', e.target.value)}
                                                        className="w-full bg-transparent font-black text-red-600 text-right outline-none"
                                                        placeholder="Amount (AED)"
                                                    />
                                                </td>
                                                <td className="px-8 py-3">
                                                    <button onClick={() => removeRow('expenseRecords', idx)} className="text-gray-300 hover:text-red-500 transition-colors">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Bottom Add Button */}
                            {(formData.expenseRecords || []).length > 0 && (
                                <div className="px-8 py-3 border-t border-gray-50 bg-gray-50/30">
                                    <button
                                        onClick={() => addRow('expenseRecords')}
                                        className="w-full py-2.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-all flex items-center justify-center gap-1.5"
                                    >
                                        <Plus size={14} /> Add Expense
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* 3. Visit Records */}
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between bg-green-50/30">
                                <h3 className="font-black text-gray-900 uppercase tracking-widest text-xs flex items-center gap-2">
                                    <MapPin size={16} className="text-green-600" />
                                    Client Visit Activity Log
                                </h3>
                                <button
                                    onClick={() => addRow('clientVisits')}
                                    className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-md shadow-green-100"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            <div className="p-6 space-y-4">
                                {(formData.clientVisits || []).map((row, idx) => (
                                    <div key={idx} className="p-6 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-4 relative group">
                                        <button
                                            onClick={() => removeRow('clientVisits', idx)}
                                            className="absolute -top-2 -right-2 w-7 h-7 bg-white text-red-500 border border-gray-100 rounded-full flex items-center justify-center shadow-lg transition-opacity opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                                        >
                                            <X size={14} />
                                        </button>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                            <div className="col-span-2 lg:col-span-1">
                                                <label className="block text-[9px] font-black text-gray-400 uppercase mb-1.5 tracking-widest flex items-center gap-2">
                                                    Client Name
                                                    <Search size={10} className="text-gray-300" />
                                                </label>
                                                <input
                                                    type="text"
                                                    value={row.clientName}
                                                    onChange={(e) => handleClientNameChange('clientVisits', idx, e.target.value)}
                                                    className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs"
                                                    placeholder={customersLoading ? "Loading customers..." : "Client Name..."}
                                                    list="customer-list"
                                                />
                                            </div>
                                            <div className="col-span-1 lg:col-span-1">
                                                <label className="block text-[9px] font-black text-gray-400 uppercase mb-1.5 tracking-widest">Mobile Number</label>
                                                <div className="relative">
                                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={12} />
                                                    <input
                                                        type="tel"
                                                        value={row.customerMobileNumber}
                                                        onChange={(e) => updateRow('clientVisits', idx, 'customerMobileNumber', e.target.value)}
                                                        className="w-full pl-8 pr-4 py-2 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs"
                                                        placeholder="05x xxx xxxx"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-span-1 lg:col-span-1">
                                                <label className="block text-[9px] font-black text-gray-400 uppercase mb-1.5 tracking-widest">Visit Type</label>
                                                <select
                                                    value={row.visitType}
                                                    onChange={(e) => updateRow('clientVisits', idx, 'visitType', e.target.value)}
                                                    className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs"
                                                >
                                                    {VISIT_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </div>
                                            <div className="flex items-center gap-3 self-end pb-1 col-span-2 lg:col-span-1">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={row.followUpRequired}
                                                        onChange={(e) => updateRow('clientVisits', idx, 'followUpRequired', e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">Follow-up</span>
                                                </label>
                                                {row.followUpRequired && (
                                                    <CustomDatePicker
                                                        mode="single"
                                                        value={row.followUpDetails}
                                                        onChange={(val) => updateRow('clientVisits', idx, 'followUpDetails', val)}
                                                        quickFilters={false}
                                                    />
                                                )}
                                            </div>
                                            <div className="col-span-1 lg:col-span-2">
                                                <label className="block text-[9px] font-black text-gray-400 uppercase mb-1.5 tracking-widest">Visit Outcome</label>
                                                <select
                                                    value={row.outcomeNotes}
                                                    onChange={(e) => updateRow('clientVisits', idx, 'outcomeNotes', e.target.value)}
                                                    className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs"
                                                >
                                                    <option value="">Select Outcome</option>
                                                    {VISIT_OUTCOMES.map(v => <option key={v} value={v}>{v}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-span-1 lg:col-span-2">
                                                <label className="block text-[9px] font-black text-gray-400 uppercase mb-1.5 tracking-widest">Activity Details</label>
                                                <input
                                                    type="text"
                                                    value={row.notes}
                                                    onChange={(e) => updateRow('clientVisits', idx, 'notes', e.target.value)}
                                                    className="w-full px-4 py-2 bg-white border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-xs"
                                                    placeholder="Brief discussion summary..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {formData.clientVisits.length === 0 && (
                                    <div className="text-center py-6 text-gray-400 italic text-xs">No client visits logged for today.</div>
                                )}
                            </div>
                            {/* Bottom Add Button */}
                            {(formData.clientVisits || []).length > 0 && (
                                <div className="px-6 pb-6">
                                    <button
                                        onClick={() => addRow('clientVisits')}
                                        className="w-full py-2.5 text-xs font-bold text-green-600 bg-green-50 hover:bg-green-100 rounded-xl transition-all flex items-center justify-center gap-1.5"
                                    >
                                        <Plus size={14} /> Add Visit
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Bottom Save Bar */}
            <div className="sticky bottom-0 z-20 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-6 py-3 flex items-center justify-between shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {currentReport ? 'Editing Entry' : 'New Entry'} — {formData.reportDate ? format(new Date(formData.reportDate + 'T00:00:00'), 'MMM dd, yyyy') : 'No date'}
                </div>
                <button
                    onClick={handleSubmit}
                    disabled={isSaving || !!existingReportForDate}
                    className={`bg-blue-600 text-white px-10 py-2.5 rounded-xl flex items-center gap-2 font-black text-xs shadow-xl hover:bg-blue-700 transition-all uppercase tracking-wider disabled:opacity-50 ${isSaving || !!existingReportForDate ? '' : 'active:scale-95'}`}
                >
                    {isSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                    Save
                </button>
            </div>
        </div>
    );

    const renderPrintPreview = () => {
        if (!showPrintPreview) return null;
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden">
                    <div className="bg-blue-600 p-6 text-white flex items-center justify-between">
                        <h2 className="text-xl font-black uppercase tracking-widest flex items-center gap-2">
                            <FileText size={20} /> Detailed Operations Log
                        </h2>
                        <button onClick={() => setShowPrintPreview(false)} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                            <X size={24} />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 sm:p-12 bg-gray-50">
                        <div className="bg-white p-4 sm:p-12 shadow-sm border border-gray-100 rounded-3xl min-h-full space-y-8">
                            {/* Print Header */}
                            <div className="flex justify-between items-start border-b border-gray-100 pb-8">
                                <div>
                                    <h1 className="text-3xl font-black text-gray-900 tracking-tight">Daily Sales Log</h1>
                                    <div className="flex items-center gap-4 mt-2">
                                        <div className="flex items-center gap-1.5 text-xs font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-full">
                                            <User size={12} /> {staffList.find(s => s.publicId === formData.salesPersonPublicId)?.name || (formData.salesPersonPublicId ? `Staff (${formData.salesPersonPublicId.slice(0, 8)})` : 'Staff Member')}
                                        </div>
                                        {formData.helperName && (
                                            <div className="flex items-center gap-1.5 text-xs font-black text-gray-500 uppercase tracking-widest bg-gray-100 px-3 py-1 rounded-full">
                                                <UserPlus size={12} /> {formData.helperName}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1.5 text-xs font-black text-gray-500 uppercase tracking-widest bg-gray-100 px-3 py-1 rounded-full">
                                            <Hash size={12} /> {formData.vehicleNo || 'No Vehicle'}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Entry Date</p>
                                    <p className="text-xl font-black text-gray-900">{safeFormatDate(formData.reportDate, 'MMM dd, yyyy')}</p>
                                    <div className="mt-2 text-[10px] font-bold text-gray-400 uppercase tracking-tighter flex items-center justify-end gap-1">
                                        <MapPin size={10} className="text-gray-400" /> Odometer: {formData.odometerStart || 0} - {formData.odometerEnd || 0} ({ (Number(formData.odometerEnd) || 0) - (Number(formData.odometerStart) || 0) } KM)
                                    </div>
                                </div>
                            </div>

                            {/* Re-use StaffReportDetailCard for EXACT consistency with Expanded Day View */}
                            <StaffReportDetailCard
                                report={formData}
                                detailedData={detailedData}
                                setDetailedData={setDetailedData}
                            />
                        </div>
                    </div>
                    <div className="p-6 bg-white border-t flex justify-end gap-3">
                        <button 
                            onClick={() => downloadConsolidatedPdf(formData.reportDate, formData.reportDate, formData.salesPersonPublicId)}
                            className="bg-blue-600 text-white px-8 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-blue-700 transition-all flex items-center gap-2"
                        >
                            <Download size={16} />
                            Download PDF
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderConfirmBackModal = () => {
        if (!showConfirmBack) return null;
        return (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-gray-100 flex flex-col space-y-4 animate-in zoom-in-95 duration-200">
                    <div className="flex items-center gap-3 text-amber-500">
                        <div className="p-2 bg-amber-50 rounded-xl">
                            <AlertCircle size={20} />
                        </div>
                        <h3 className="text-sm font-black uppercase tracking-wider text-gray-900">Unsaved Changes</h3>
                    </div>
                    <p className="text-xs font-bold text-gray-500 leading-relaxed">
                        You have unsaved changes in this ledger. Are you sure you want to go back? Any unsaved changes will be lost.
                    </p>
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            onClick={() => setShowConfirmBack(false)}
                            className="px-5 py-2.5 bg-gray-50 text-gray-500 rounded-xl font-black text-[10px] uppercase tracking-wider hover:bg-gray-100 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                setView('list');
                                setShowConfirmBack(false);
                            }}
                            className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-black text-[10px] uppercase tracking-wider shadow-lg shadow-amber-100 transition-all"
                        >
                            Discard
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const safeRender = () => {
        try {
            return (
                <div className="h-full overflow-hidden bg-white">
                    {view === 'list' ? renderListView() : renderFormView()}
                    {renderPrintPreview()}
                    {renderConfirmBackModal()}
                    
                    {/* Global Customer Datalist */}
                    <datalist id="customer-list">
                        {(customers || []).map(c => (
                            <option key={c.publicId} value={c.name} />
                        ))}
                    </datalist>
                </div>
            );
        } catch (e) {
            console.error("DailySalesReportView render crash:", e);
            return (
                <div className="p-8 flex flex-col items-center justify-center h-full bg-red-50 text-red-600">
                    <AlertCircle size={48} className="mb-4" />
                    <h2 className="text-xl font-black uppercase tracking-tighter mb-2">Ops Log Rendering Error</h2>
                    <p className="text-sm font-medium mb-6">Something went wrong while displaying this page.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-red-600 text-white px-6 py-2 rounded-xl font-black text-xs uppercase tracking-widest"
                    >
                        Reload Application
                    </button>
                </div>
            );
        }
    };

    return safeRender();
};

export default DailySalesReportView;
